import { useMemo, useState, useCallback } from 'react';
import { featureBoardTypeKey, isFeatureHidden } from '../utils/featureBoardRows';

const normalizeText = (value) => (value || '').toString().toLowerCase();

export function useFeatureFilters(features = [], { hiddenFeatureIds } = {}) {
  const [searchText, setSearchText] = useState('');
  const [activeTypes, setActiveTypes] = useState(() => new Set());
  const [hiddenOnly, setHiddenOnly] = useState(false);

  const toggleType = useCallback((type) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const toggleHidden = useCallback(() => {
    setHiddenOnly((prev) => !prev);
  }, []);

  const showAll = useCallback(() => {
    setActiveTypes(new Set());
    setHiddenOnly(false);
  }, []);

  const clearFilters = useCallback(() => {
    setActiveTypes(new Set());
    setSearchText('');
    setHiddenOnly(false);
  }, []);

  const filtered = useMemo(() => {
    const needle = normalizeText(searchText);
    const hasTypeFilter = activeTypes.size > 0;

    return (features || []).filter((f) => {
      const type = featureBoardTypeKey(f);
      if (hasTypeFilter && !activeTypes.has(type)) return false;
      if (hiddenOnly && !isFeatureHidden(f, hiddenFeatureIds)) return false;

      if (!needle) return true;
      const haystack = [
        f?.name,
        f?.notes,
        f?.marker_type,
        f?.icon,
      ].map(normalizeText).join(' ');
      return haystack.includes(needle);
    });
  }, [features, searchText, activeTypes, hiddenOnly, hiddenFeatureIds]);

  return {
    filteredFeatures: filtered,
    searchText,
    setSearchText,
    activeTypes,
    toggleType,
    hiddenOnly,
    toggleHidden,
    showAll,
    clearFilters,
  };
}
